/*
 * Native search API wrapper — shared helpers for the native replacement of
 * ConvertCart IntelliSearch. All native search UI files (header input, focus
 * popup, typing dropdown, mobile overlay, results page) should use this and
 * never call fetch() or touch localStorage directly. Vanilla JS, no deps.
 *
 * Exposes window.AroSearchAPI with:
 *   fetchProductSuggestions(query, opts)    -> Promise<Array<product>>
 *   fetchArticleSuggestions(query, opts)    -> Promise<Array<article>>
 *   fetchQuerySuggestions(query, opts)      -> Promise<Array<{text}>>
 *   fetchAll(query, opts)                   -> Promise<{products, articles, queries}>
 *   getRecentSearches()                     -> Array<string>
 *   addRecentSearch(query, resultCount)     -> void  (zero-result queries are ignored)
 *   removeRecentSearch(query)               -> void
 *   clearRecentSearches()                   -> void
 *   trackSearchEvent(query, opts)           -> void  (fires GTM dataLayer + gtag)
 *   trackAroSearchEvent(eventType, payload) -> void  (posts directly to n8n
 *                                                     search-events-ingest
 *                                                     webhook + pushes to
 *                                                     dataLayer for parallel
 *                                                     observers. Rate-limited
 *                                                     to 10 events/sec/tab.)
 *
 * Why a new localStorage key (not CC's `recentSearches`):
 *   - CC's history is polluted with zero-result queries (a UX bug we're not
 *     replicating). Fresh key = clean start; no need to migrate/dedupe.
 *   - Namespaced under `aro_` matches the sibling exit-search-popup.js pattern.
 *
 * See Dev/cc-intellisearch-replacement/SPEC.md for the full contract.
 */
(function () {
  'use strict';

  var RECENT_KEY = 'aro_recent_searches';
  // Storage cap: how many entries we KEEP in localStorage. Display cap
  // (top N shown in the UI) is smaller and defined in native-search-input.js
  // as RECENT_LIMIT. Keep storage > display so users still have a small
  // trailing history if the display cap is bumped later. Design intent
  // per HANDOFF: "top 6 shown, up to 10 stored".
  var RECENT_MAX = 10;
  var SUGGEST_URL = '/search/suggest.json';

  // -------------------------------------------------------------------------
  // Fetchers — wrap Shopify's built-in /search/suggest.json.
  // Response shape: { resources: { results: { products: [], articles: [], queries: [] } } }
  // -------------------------------------------------------------------------

  function buildSuggestUrl(query, type, limit) {
    var q = encodeURIComponent(String(query || '').trim());
    var lim = Math.max(1, Math.min(10, parseInt(limit, 10) || 6));
    return SUGGEST_URL
      + '?q=' + q
      + '&resources[type]=' + type
      + '&resources[limit]=' + lim
      + '&resources[options][unavailable_products]=last';
  }

  function fetchResourceType(query, type, opts) {
    opts = opts || {};
    if (!query || !String(query).trim()) {
      return Promise.resolve([]);
    }
    var url = buildSuggestUrl(query, type, opts.limit);
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('suggest ' + type + ' HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        var results = (data && data.resources && data.resources.results) || {};
        return results[type + 's'] || [];
      })
      .catch(function (err) {
        // Fail-soft: log for DevTools visibility, return empty so UI can render
        // a graceful zero-results state instead of crashing.
        if (window.console && window.console.warn) {
          window.console.warn('[AroSearchAPI] fetch ' + type + ' failed:', err);
        }
        return [];
      });
  }

  function fetchProductSuggestions(query, opts) {
    return fetchResourceType(query, 'product', opts);
  }

  function fetchArticleSuggestions(query, opts) {
    return fetchResourceType(query, 'article', opts);
  }

  function fetchQuerySuggestions(query, opts) {
    return fetchResourceType(query, 'query', opts);
  }

  // Convenience: fire all three in parallel (matches CC's dropdown fan-out).
  function fetchAll(query, opts) {
    opts = opts || {};
    var productLimit = opts.productLimit || opts.limit || 6;
    var articleLimit = opts.articleLimit || opts.limit || 6;
    var queryLimit = opts.queryLimit || 4;
    return Promise.all([
      fetchProductSuggestions(query, { limit: productLimit }),
      fetchArticleSuggestions(query, { limit: articleLimit }),
      fetchQuerySuggestions(query, { limit: queryLimit })
    ]).then(function (results) {
      return { products: results[0], articles: results[1], queries: results[2] };
    });
  }

  // -------------------------------------------------------------------------
  // Recent searches — localStorage-backed, dedup + zero-result filter.
  // -------------------------------------------------------------------------

  function readRecent() {
    try {
      var raw = window.localStorage.getItem(RECENT_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeRecent(list) {
    try {
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch (e) {
      // localStorage disabled (private mode / quota / SecurityError) — silent noop.
      // Recent searches are convenience, not correctness.
    }
  }

  function normalize(query) {
    return String(query || '').trim();
  }

  function getRecentSearches() {
    return readRecent();
  }

  function addRecentSearch(query, resultCount) {
    var q = normalize(query);
    if (!q) return;
    // Filter zero-result queries — matches SPEC FR-003 (fix for CC's UX bug).
    if (typeof resultCount === 'number' && resultCount <= 0) return;
    var list = readRecent();
    // Case-insensitive dedup: remove any existing entry with the same
    // normalized form, then unshift so the newest is first.
    var lower = q.toLowerCase();
    list = list.filter(function (item) {
      return String(item).toLowerCase() !== lower;
    });
    list.unshift(q);
    if (list.length > RECENT_MAX) list.length = RECENT_MAX;
    writeRecent(list);
  }

  function removeRecentSearch(query) {
    var lower = normalize(query).toLowerCase();
    if (!lower) return;
    var list = readRecent().filter(function (item) {
      return String(item).toLowerCase() !== lower;
    });
    writeRecent(list);
  }

  function clearRecentSearches() {
    writeRecent([]);
  }

  // -------------------------------------------------------------------------
  // Open filter groups — mirror the open/closed state of filter categories
  // (Collection, Availability, Price, Size, etc.) between the header
  // predictive-search dropdown and the /search page sidebar. Set stored in
  // localStorage; null return = uninitialised (surfaces use their own
  // defaults until the user's first toggle seeds the set).
  // -------------------------------------------------------------------------

  var OPEN_GROUPS_KEY = 'aro_open_filter_groups';

  function getOpenFilterGroups() {
    try {
      var raw = window.localStorage.getItem(OPEN_GROUPS_KEY);
      if (!raw) return null;
      var arr = JSON.parse(raw);
      return Array.isArray(arr) ? new Set(arr) : null;
    } catch (e) { return null; }
  }

  function setOpenFilterGroups(setOrArray) {
    try {
      var arr = Array.from(setOrArray);
      window.localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(arr));
      // Same-tab notify so the other surface (dropdown or /search) can
      // re-apply without waiting for a full page reload.
      try { window.dispatchEvent(new Event('aro:open-groups-updated')); } catch (e) {}
    } catch (e) { /* localStorage full / private mode — silent noop */ }
  }

  // -------------------------------------------------------------------------
  // Analytics — GTM dataLayer + gtag parity with CC's `intelliSearch` event.
  // -------------------------------------------------------------------------

  // Strict CC parity — fire exactly what CC's bundle used to fire, nothing
  // more. CC's push was `{event: 'intelliSearch', search_term: q}` via both
  // dataLayer and gtag, and only on header form submit (see
  // predictive-search.js onFormSubmit). Do not add source tags or modern
  // event names; Benj's GA reports are keyed on the CC event shape and
  // shouldn't see any drift.
  function trackSearchEvent(query) {
    var q = normalize(query);
    if (!q) return;
    var payload = { event: 'intelliSearch', search_term: q };
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (e) {}
    if (typeof window.gtag === 'function') {
      try { window.gtag('event', 'intelliSearch', { search_term: q }); } catch (e) {}
    }
  }

  // -------------------------------------------------------------------------
  // Aromantic-owned search analytics — pushes envelope events to GTM's
  // dataLayer for the `aro_search` custom event, which Benj's GTM tag then
  // forwards to n8n webhook `search-events-ingest` → AroExtra `search_events`
  // table. See Dev/cc-intellisearch-replacement/docs/SPEC-search-analytics.md
  // §§ 3, 4, 7.1 for the envelope shape + session identity model. This is
  // separate from CC-parity trackSearchEvent above — that stays strict-parity
  // so Benj's GA reports don't drift; this is our own instrumentation.
  // -------------------------------------------------------------------------

  var SESSION_ID_KEY = 'aro_session_id';

  function pad2(n) { return (n < 10 ? '0' : '') + n; }

  // Return ISO-8601 timestamp with London-local offset (+01:00 BST or
  // +00:00 GMT). Team convention: never store/emit UTC zulu (`...Z`).
  function londonISOString() {
    var d = new Date();
    // 'sv-SE' locale renders as "YYYY-MM-DD HH:MM:SS" 24h format
    var wall = d.toLocaleString('sv-SE', { timeZone: 'Europe/London' });
    var iso = wall.replace(' ', 'T');
    // Compute offset: parse London wall-clock as if UTC, subtract actual UTC
    var londonAsUTC = new Date(iso + 'Z').getTime();
    var offsetMinutes = Math.round((londonAsUTC - d.getTime()) / 60000);
    var sign = offsetMinutes >= 0 ? '+' : '-';
    var absMin = Math.abs(offsetMinutes);
    return iso + sign + pad2(Math.floor(absMin / 60)) + ':' + pad2(absMin % 60);
  }

  function getOrCreateAroSessionId() {
    try {
      var existing = window.localStorage.getItem(SESSION_ID_KEY);
      if (existing) return existing;
      var id;
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        id = window.crypto.randomUUID();
      } else {
        // Fallback for browsers without crypto.randomUUID (pre-2022 Safari, etc.)
        id = 'aro-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
      }
      window.localStorage.setItem(SESSION_ID_KEY, id);
      return id;
    } catch (e) {
      // localStorage disabled (private mode etc.) — return an ephemeral ID
      // so this fire is still logged, next fire will get a fresh one
      return 'ephemeral-' + Date.now();
    }
  }

  function getShopifyCookie(name) {
    try {
      var pairs = String(document.cookie || '').split('; ');
      for (var i = 0; i < pairs.length; i++) {
        var eq = pairs[i].indexOf('=');
        if (eq > 0 && pairs[i].slice(0, eq) === name) {
          return decodeURIComponent(pairs[i].slice(eq + 1));
        }
      }
    } catch (e) {}
    return null;
  }

  // Direct-to-n8n transport. Bypasses GTM entirely — cheaper to ship and
  // no dependency on GTM container access. dataLayer.push still happens in
  // parallel so any future GTM / Elevar / observer can pick events up.
  var INGEST_URL = 'https://n8n.aromantic.co.uk/webhook/aro-search-events';

  // Rolling 1-second rate limiter — max N events sent to the ingest per tab.
  // Silent-drops anything above the ceiling; the dataLayer push still fires
  // so local observers see everything. Ceiling of 10/sec is well above any
  // human peak (debounced typing tops out at ~3/sec + fastest clicks ~5/sec)
  // but far below bot / scripted attack rates. Per-tab, not per-session —
  // localStorage-coordinated rate-limiting would add latency to every event
  // for marginal benefit. If a scripted attack lands in one tab, that tab
  // is throttled; if it lands in multiple tabs, each is throttled independently.
  //
  // Drops are NOT silent to downstream analytics — the per-event-type count
  // of drops-since-last-successful-send is attached to the very next send
  // that passes the limit (in payload.throttled_since_last). Downstream
  // reports can flag sessions with high drop counts as bot / spam signals
  // even when the dropped events themselves never landed.
  var RATE_LIMIT_PER_SEC = 10;
  var recentSendTimestamps = [];
  var droppedCounts = {}; // { eventType: count } since last successful send

  function withinRateLimit(eventType) {
    var now = Date.now();
    var cutoff = now - 1000;
    // Prune old entries in-place
    while (recentSendTimestamps.length && recentSendTimestamps[0] <= cutoff) {
      recentSendTimestamps.shift();
    }
    if (recentSendTimestamps.length >= RATE_LIMIT_PER_SEC) {
      droppedCounts[eventType] = (droppedCounts[eventType] || 0) + 1;
      return false;
    }
    recentSendTimestamps.push(now);
    return true;
  }

  // Return + clear accumulated drop counters, or null if nothing dropped.
  function drainDroppedCounts() {
    var out = null;
    for (var k in droppedCounts) {
      if (Object.prototype.hasOwnProperty.call(droppedCounts, k) && droppedCounts[k] > 0) {
        if (!out) out = {};
        out[k] = droppedCounts[k];
      }
    }
    droppedCounts = {};
    return out;
  }

  // Send envelope to n8n ingest via sendBeacon (preferred — no CORS preflight,
  // survives page unload for terminal click events) with fetch fallback for
  // browsers where sendBeacon isn't available or returns false (rare, e.g.
  // very large payloads on some Firefox versions).
  //
  // Body content-type is text/plain — avoids the CORS preflight OPTIONS
  // request that application/json would trigger. The n8n workflow's Code
  // node parses the string body via JSON.parse. Cross-origin friendly with
  // zero n8n CORS config required.
  function sendToIngest(envelope) {
    var body;
    try { body = JSON.stringify(envelope); }
    catch (e) { return; /* unserialisable envelope — silent drop */ }

    try {
      if (typeof navigator.sendBeacon === 'function') {
        var blob = new Blob([body], { type: 'text/plain' });
        var ok = navigator.sendBeacon(INGEST_URL, blob);
        if (ok) return;
        // beacon returned false (queue full / too large) → fall through to fetch
      }
    } catch (e) { /* fall through to fetch */ }

    try {
      fetch(INGEST_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: body,
        keepalive: true  // survives page unload — critical for click events
      }).catch(function () { /* fire-and-forget */ });
    } catch (e) { /* silent */ }
  }

  // Generate a per-event UUID for the search-intent tracker (Phase F.1).
  // Client-generated (not DB-assigned) so the browser can reference raw
  // events by uuid in the search_intents.event_uuids_json array without
  // a server round-trip to learn the DB auto-inc id.
  function newEventUuid() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
    } catch (e) {}
    // Fallback for browsers without crypto.randomUUID (rare in 2026+)
    return 'aro-evt-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
  }

  function trackAroSearchEvent(eventType, payload) {
    if (!eventType) return;
    var envelope = {
      event: 'aro_search',
      aro_event_type: eventType,
      aro_session_id: getOrCreateAroSessionId(),
      shopify_y: getShopifyCookie('_shopify_y'),
      shopify_s: getShopifyCookie('_shopify_s'),
      occurred_at: londonISOString(),
      // Phase F.1: per-event UUID for intent-tracker joins. Every raw event
      // carries one; the intent tracker (Phase F.2) accumulates uuids for
      // the in-progress intent and fires them in the terminal search_intent
      // event's payload.event_uuids array.
      event_uuid: newEventUuid(),
      page_url: window.location.href,
      payload: payload || {}
    };

    // Always push to dataLayer — cheap, gives future observers full stream.
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(envelope);
    } catch (e) { /* analytics failure must never break search UX */ }

    // Phase F.2: intent-tracker observation. Fires terminal search_intent
    // event if this raw event marks the end of an intent (click / new_search
    // / etc.). Recursion guard inside observe() skips events of type
    // 'search_intent' so our own terminal fire re-entering here is a no-op.
    // Tracker failure must never break search UX — try/catch swallows.
    try {
      if (window.AroIntentTracker && typeof window.AroIntentTracker.observe === 'function') {
        window.AroIntentTracker.observe(envelope);
      }
    } catch (e) { /* tracker failure must never break search UX */ }

    // Rate-limit only the actual send to n8n. If over ceiling, silent drop
    // (but count the drop by event_type — see below): dataLayer still has
    // the event so nothing local is lost, we just don't hammer the ingest.
    if (!withinRateLimit(eventType)) return;

    // Attach any drops accumulated since the last successful send. This
    // means downstream reports see "N dropdown_query events were dropped
    // between the last one we saw and this one" — bot-signal preserved
    // even for the events we didn't send. Cleared on read (drainDroppedCounts).
    var dropped = drainDroppedCounts();
    if (dropped) {
      // Nest under payload so it lands in payload_json without needing a
      // new indexed column. Clone the payload object to avoid mutating the
      // caller's reference.
      envelope.payload = Object.assign({}, envelope.payload, { throttled_since_last: dropped });
    }

    sendToIngest(envelope);
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  window.AroSearchAPI = {
    fetchProductSuggestions: fetchProductSuggestions,
    fetchArticleSuggestions: fetchArticleSuggestions,
    fetchQuerySuggestions: fetchQuerySuggestions,
    fetchAll: fetchAll,
    getRecentSearches: getRecentSearches,
    addRecentSearch: addRecentSearch,
    removeRecentSearch: removeRecentSearch,
    clearRecentSearches: clearRecentSearches,
    getOpenFilterGroups: getOpenFilterGroups,
    setOpenFilterGroups: setOpenFilterGroups,
    trackSearchEvent: trackSearchEvent,
    trackAroSearchEvent: trackAroSearchEvent
  };
})();
